import type { Firm } from '@domain/firm/model';

/// 所属事務所 inline select 用の選択肢。表示は label、保存は value (firm_id)。
/// InlineField の SelectOption と構造的に互換 (value: string / label: string)。
export interface FirmSelectOption {
  value: string;
  label: string;
}

/// 現在所属事務所の状態。`withCurrentFirmOption` が補完 option を決めるのに使う。
export interface CurrentFirmState {
  /// 解決済みの所属事務所。表示ラベルは official_name。未取得 / 取得失敗なら null。
  affiliatedFirm: Pick<Firm, 'id' | 'official_name'> | null | undefined;
  /// 保存値 lawyers.firm_id。所属なしなら null。
  firmId: string | null | undefined;
  /// affiliatedFirm 取得に失敗したか。失敗のときだけ「取得できません」、それ以外
  /// (取得中 / 未開始) は失敗と断定せず「取得中…」にする。
  hasError: boolean;
}

/// 所属事務所 inline select の選択肢に、現在所属している事務所を補完する。
///
/// `firmOptions` は `firmOptionsStore` (listFirms, page_size 上限 500) 由来なので、
/// 現在所属事務所が取得ページに含まれないことがある。その場合 InlineField の select は
/// value (firm_id UUID) を label 解決できず raw UUID を表示してしまう (#570 / spec #569)。
/// これは `affiliatedFirm` 取得中 / 取得失敗の間も同じく起きる。
///
/// firm_id が `firmOptions` に**無い場合だけ**、value が firm_id に一致する option を
/// 先頭に補完する。これにより InlineField の displayFor が firm_id を label 解決でき、
/// どの状態でも raw UUID を主表示に出さない。
///
/// - `affiliatedFirm` 解決済み → label は事務所名 (official_name)
/// - 取得中 / 未開始 → label は「取得中…」
/// - 取得失敗 → label は「事務所名を取得できません」
///   (文言は弁護士詳細ヘッダー (#571) と揃える。共通化は PR-D の整合 QA で判断)
///
/// firm_id が既に `firmOptions` にある (取得ページ内) 場合は補完しない。実名がそこで
/// 解決されるため、暫定文言で上書きしない。所属なし (firmId なし) もそのまま返す。
///
/// 保存値 `lawyers.firm_id` の意味は不変 (option は表示ラベル解決のためだけに足す)。
/// DOM / store / Svelte に依存しない純粋関数で、元配列は破壊しない。
export function withCurrentFirmOption(
  firmOptions: FirmSelectOption[],
  current: CurrentFirmState,
): FirmSelectOption[] {
  const { affiliatedFirm, firmId, hasError } = current;

  // 解決済み: 事務所名で補完。
  if (affiliatedFirm) {
    if (firmOptions.some((o) => o.value === affiliatedFirm.id)) return firmOptions;
    return [
      { value: affiliatedFirm.id, label: affiliatedFirm.official_name },
      ...firmOptions,
    ];
  }

  // 未解決 (取得中 / 取得失敗 / 未開始): firm_id が候補に無ければ UUID 露出を防ぐ
  // 暫定 option を補完する。失敗と断定できるのは hasError のときだけ。
  if (!firmId) return firmOptions;
  if (firmOptions.some((o) => o.value === firmId)) return firmOptions;
  const pendingLabel = hasError ? '事務所名を取得できません' : '取得中…';
  return [{ value: firmId, label: pendingLabel }, ...firmOptions];
}
